import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react'
import type { CommentRegion } from '../types'

/** A comment with an optional drawn region, for rendering boxes over the photo. */
export interface RegionComment {
  id: string
  region?: CommentRegion | null
}

interface AnnotationLayerProps {
  /**
   * The image's intrinsic size. The overlay fills the same box as the
   * object-fit:contain image, so it recomputes the letterboxed content rect
   * from its own measured size + this aspect — pointer math then lands exactly
   * on the visible pixels, never on the letterbox bars.
   */
  naturalWidth: number
  naturalHeight: number
  /** Existing comments; only those with a region draw a box. */
  comments: RegionComment[]
  /** The currently selected/hovered comment id (its box is highlighted). */
  activeId?: string | null
  /** Click an existing box → select that comment. */
  onSelectRegion?: (id: string) => void
  /**
   * When true the layer captures pointer drags to draw a new box. On commit it
   * reports the normalized region; a click without drag reports a point
   * marker (w = h = 0). When false the layer is pass-through except for the
   * existing boxes' own click targets.
   */
  drawing: boolean
  onDrawComplete?: (region: CommentRegion) => void
}

/** A finished drag is only a box (not a stray click) past this px threshold. */
const MIN_DRAG_PX = 6

interface ContentRect {
  ox: number
  oy: number
  dw: number
  dh: number
}

/**
 * Compute the object-fit:contain content box (letterbox offsets + displayed
 * size) for an intrinsic image inside a container. Mirrors the browser's own
 * contain layout so a normalized [0,1] region maps to the exact visible pixels.
 */
function contentRect(
  containerW: number,
  containerH: number,
  naturalWidth: number,
  naturalHeight: number
): ContentRect | null {
  if (containerW <= 0 || containerH <= 0 || naturalWidth <= 0 || naturalHeight <= 0) {
    return null
  }
  const scale = Math.min(containerW / naturalWidth, containerH / naturalHeight)
  const dw = naturalWidth * scale
  const dh = naturalHeight * scale
  return { ox: (containerW - dw) / 2, oy: (containerH - dh) / 2, dw, dh }
}

const clamp01 = (n: number) => (n < 0 ? 0 : n > 1 ? 1 : n)

/**
 * Markup overlay for the client review photo. Draws each comment's region as a
 * box over the image, highlights the active one, and (in `drawing` mode) lets
 * the reviewer drag out a new normalized region. All coords are normalized
 * [0,1] relative to the image's intrinsic frame, so they survive any display
 * size and the object-fit:contain letterbox.
 */
export function AnnotationLayer({
  naturalWidth,
  naturalHeight,
  comments,
  activeId,
  onSelectRegion,
  drawing,
  onDrawComplete,
}: AnnotationLayerProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ w: 0, h: 0 })
  // The in-progress drag, in container px (null when not dragging).
  const [dragRect, setDragRect] = useState<{ x: number; y: number; w: number; h: number } | null>(
    null
  )
  const dragStart = useRef<{ x: number; y: number } | null>(null)

  // Track the rendered overlay size so we can recompute the content box.
  useLayoutEffect(() => {
    const el = rootRef.current
    if (!el) return
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight })
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const rect = contentRect(size.w, size.h, naturalWidth, naturalHeight)

  const pointerToContent = useCallback(
    (e: ReactPointerEvent): { px: number; py: number } | null => {
      const el = rootRef.current
      if (!el) return null
      const b = el.getBoundingClientRect()
      return { px: e.clientX - b.left, py: e.clientY - b.top }
    },
    []
  )

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent) => {
      if (!drawing || !rect) return
      const p = pointerToContent(e)
      if (!p) return
      // Ignore presses that start outside the image content (on the letterbox).
      if (p.px < rect.ox || p.px > rect.ox + rect.dw) return
      if (p.py < rect.oy || p.py > rect.oy + rect.dh) return
      e.currentTarget.setPointerCapture(e.pointerId)
      dragStart.current = { x: p.px, y: p.py }
      setDragRect({ x: p.px, y: p.py, w: 0, h: 0 })
    },
    [drawing, rect, pointerToContent]
  )

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent) => {
      if (!dragStart.current || !rect) return
      const p = pointerToContent(e)
      if (!p) return
      // Clamp the live corner to the image content box.
      const cx = Math.min(Math.max(p.px, rect.ox), rect.ox + rect.dw)
      const cy = Math.min(Math.max(p.py, rect.oy), rect.oy + rect.dh)
      const s = dragStart.current
      setDragRect({
        x: Math.min(s.x, cx),
        y: Math.min(s.y, cy),
        w: Math.abs(cx - s.x),
        h: Math.abs(cy - s.y),
      })
    },
    [rect, pointerToContent]
  )

  const handlePointerUp = useCallback(() => {
    const s = dragStart.current
    const live = dragRect
    dragStart.current = null
    setDragRect(null)
    if (!s || !live || !rect || !onDrawComplete) return
    // A drag shorter than the threshold in both axes is a point marker.
    const isPoint = live.w < MIN_DRAG_PX && live.h < MIN_DRAG_PX
    const nx = clamp01((live.x - rect.ox) / rect.dw)
    const ny = clamp01((live.y - rect.oy) / rect.dh)
    if (isPoint) {
      onDrawComplete({ x: nx, y: ny, w: 0, h: 0 })
      return
    }
    const nw = clamp01(live.w / rect.dw)
    const nh = clamp01(live.h / rect.dh)
    onDrawComplete({ x: nx, y: ny, w: Math.min(nw, 1 - nx), h: Math.min(nh, 1 - ny) })
  }, [dragRect, rect, onDrawComplete])

  // Convert a stored normalized region to absolute container px for rendering.
  const regionStyle = (r: CommentRegion): CSSProperties | null => {
    if (!rect) return null
    const isPoint = r.w === 0 && r.h === 0
    if (isPoint) {
      const cx = rect.ox + r.x * rect.dw
      const cy = rect.oy + r.y * rect.dh
      return { left: cx - 7, top: cy - 7, width: 14, height: 14, borderRadius: '9999px' }
    }
    return {
      left: rect.ox + r.x * rect.dw,
      top: rect.oy + r.y * rect.dh,
      width: r.w * rect.dw,
      height: r.h * rect.dh,
    }
  }

  return (
    <div
      ref={rootRef}
      data-testid="annotation-layer"
      className="absolute inset-0"
      style={{ touchAction: drawing ? 'none' : undefined, cursor: drawing ? 'crosshair' : undefined }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      {comments.map((c) => {
        if (!c.region) return null
        const s = regionStyle(c.region)
        if (!s) return null
        const active = c.id === activeId
        return (
          <button
            key={c.id}
            type="button"
            data-testid={`annotation-region-${c.id}`}
            aria-label="Comment region"
            aria-pressed={active}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              onSelectRegion?.(c.id)
            }}
            className="absolute pointer-events-auto"
            style={{
              ...s,
              border: `2px solid ${active ? 'var(--accent-primary)' : 'rgba(255,255,255,0.85)'}`,
              boxShadow: active
                ? '0 0 0 9999px rgba(0,0,0,0.28)'
                : '0 0 0 1px rgba(0,0,0,0.5)',
              background: active ? 'rgba(255,255,255,0.10)' : 'transparent',
            }}
          />
        )
      })}

      {dragRect && (
        <div
          data-testid="annotation-draft"
          className="absolute pointer-events-none"
          style={{
            left: dragRect.x,
            top: dragRect.y,
            width: dragRect.w,
            height: dragRect.h,
            border: '2px solid var(--accent-primary)',
            background: 'rgba(255,255,255,0.08)',
          }}
        />
      )}
    </div>
  )
}
